/**
 * Generación de expresiones NCalc para los diálogos por columna (§5.2, RN-16).
 * Formato y Transformación son MUTUAMENTE EXCLUYENTES: cada uno produce la
 * `expresion` de la columna y guarda su estado de UI (`formato` / `mapaValores`)
 * para poder reabrir el diálogo con los valores elegidos. `[valor]` = valor
 * propio de la columna.
 */

// ---- Formato (granular, como el legado) ----

export type FormatoTipo = "numero" | "moneda" | "fecha" | "texto" | "ninguno";
export type CasoTexto = "none" | "upper" | "lower" | "title";
export type OrdenFecha = "dmy" | "ymd";

export interface FormatoConfig {
  tipo: FormatoTipo;
  /** número/moneda: nº de decimales (0-6) + separador de miles. */
  decimales: number;
  miles: boolean;
  /** fecha: qué partes mostrar + mes como nombre + orden. */
  anio: boolean;
  mes: boolean;
  dia: boolean;
  hora: boolean;
  min: boolean;
  seg: boolean;
  mesNombre: boolean;
  orden: OrdenFecha;
  /** texto: caso + recortar espacios. */
  caso: CasoTexto;
  trim: boolean;
}

export function formatoVacio(tipo: FormatoTipo = "ninguno"): FormatoConfig {
  return {
    tipo,
    decimales: 2,
    miles: true,
    anio: true,
    mes: true,
    dia: true,
    hora: false,
    min: false,
    seg: false,
    mesNombre: false,
    orden: "dmy",
    caso: "none",
    trim: false,
  };
}

const FORMATO_PREFIJO = "fmt:";

function patronFecha(c: FormatoConfig): string {
  const sep = c.mesNombre ? " " : c.orden === "ymd" ? "-" : "/";
  const partesFecha = (c.orden === "ymd" ? (["anio", "mes", "dia"] as const) : (["dia", "mes", "anio"] as const))
    .filter((p) => c[p])
    .map((p) => (p === "anio" ? "yyyy" : p === "mes" ? (c.mesNombre ? "MMMM" : "MM") : "dd"));
  const partesHora = (["hora", "min", "seg"] as const)
    .filter((p) => c[p])
    .map((p) => (p === "hora" ? "HH" : p === "min" ? "mm" : "ss"));
  return [partesFecha.join(sep), partesHora.join(":")].filter((x) => x).join(" ");
}

export function construirFormato(cfg: FormatoConfig): { expresion: string | null; formato: string } {
  const marca = `${FORMATO_PREFIJO}${JSON.stringify(cfg)}`;
  let expresion: string | null;
  switch (cfg.tipo) {
    case "numero":
    case "moneda": {
      const dec = Math.max(0, Math.min(6, cfg.decimales ?? 2));
      const pat = cfg.tipo === "moneda" ? `C${dec}` : `${cfg.miles ? "N" : "F"}${dec}`;
      expresion = `FORMATNUMBER([valor],'${pat}')`;
      break;
    }
    case "fecha": {
      const pat = patronFecha(cfg);
      expresion = pat ? `FORMATDATE([valor],'${pat}')` : null;
      break;
    }
    case "texto": {
      let inner = "[valor]";
      if (cfg.trim) inner = `TRIM(${inner})`;
      if (cfg.caso === "upper") inner = `UPPER(${inner})`;
      else if (cfg.caso === "lower") inner = `LOWER(${inner})`;
      else if (cfg.caso === "title") inner = `PROPER(${inner})`;
      expresion = inner === "[valor]" ? null : inner;
      break;
    }
    default:
      expresion = null;
  }
  return { expresion, formato: cfg.tipo === "ninguno" ? "" : marca };
}

export function parseFormato(formato: string | null | undefined): FormatoConfig {
  if (formato && formato.startsWith(FORMATO_PREFIJO)) {
    try {
      const cfg = JSON.parse(formato.slice(FORMATO_PREFIJO.length)) as Partial<FormatoConfig>;
      return { ...formatoVacio(cfg.tipo ?? "ninguno"), ...cfg };
    } catch {
      /* formato antiguo o inválido */
    }
  }
  return formatoVacio("ninguno");
}

// ---- Transformación (mapa valor → texto) ----

export interface ParMapa {
  valor: string;
  texto: string;
}

function escaparNCalc(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

/** Construye un `if(...)` anidado y serializa el mapa como estado de UI (JSON). */
export function construirTransformacion(pares: ParMapa[]): { expresion: string | null; mapaValores: string } {
  const validos = pares.filter((p) => p.valor.trim() !== "");
  const mapaValores = JSON.stringify(validos);
  if (validos.length === 0) return { expresion: null, mapaValores };

  const expresion = validos.reduceRight(
    (acc, p) => `if([valor]='${escaparNCalc(p.valor)}','${escaparNCalc(p.texto)}',${acc})`,
    "[valor]"
  );
  return { expresion, mapaValores };
}

export function parseMapaValores(mapaValores: string | null | undefined): ParMapa[] {
  if (!mapaValores) return [];
  try {
    const arr = JSON.parse(mapaValores) as ParMapa[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
